import React from "react";
import { Button } from "@material-ui/core";
import CommonDialog from "./CommonDialog";
import PlayerAvatar from "./PlayerAvatar";

const GAME_THEME_COLORS = {
  red: '#dc3545',
  green: '#15803d',
  blue: '#2563eb'
};

const getThemeColor = (playerColor) => {
  if (!playerColor) return GAME_THEME_COLORS.green;
  
  const hex = playerColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  if (r > g && r > b) return GAME_THEME_COLORS.red;
  if (b > r && b > g) return GAME_THEME_COLORS.blue;
  return GAME_THEME_COLORS.green;
};

const ColorPicker = ({ colors, selectedColor, themeColor, onSelectColor, onRandomize }) => {
  return (
    <div className="color-picker-container">
      <div className="lobby-section-label">Choose Color</div>
      <div className="color-picker-grid">
        {colors.length > 0 ? colors.map((color) => {
          const isSelected = selectedColor === color;
          const colorTheme = getThemeColor(color);
          return (
            <button
              key={color}
              onClick={() => onSelectColor(color)}
              className={`color-picker-button ${isSelected ? 'selected' : ''}`}
              style={{
                backgroundColor: color,
                borderColor: isSelected ? colorTheme : `${colorTheme}40`,
                borderWidth: isSelected ? '4px' : '3px',
                boxShadow: isSelected 
                  ? `0 0 0 2px white, 0 0 0 4px ${colorTheme}, 0 4px 12px ${colorTheme}60` 
                  : `0 2px 4px ${colorTheme}30`,
              }}
              title={color}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.boxShadow = `0 4px 8px ${colorTheme}50`;
                  e.currentTarget.style.border = `3px solid ${colorTheme}80`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.boxShadow = `0 2px 4px ${colorTheme}30`;
                  e.currentTarget.style.border = `3px solid ${colorTheme}40`;
                }
              }}
            />
          );
        }) : (
          <div className="lobby-loading-text">Loading colors...</div>
        )}
      </div>
      <div className="color-picker-randomize">
        <Button
          variant="outlined"
          size="small"
          onClick={onRandomize}
          className="lobby-button-small"
          style={{
            borderColor: themeColor,
            color: themeColor,
          }}
        >
          🎲 Randomize Color
        </Button>
      </div>
    </div>
  );
};

const EmojiPicker = ({ emojis, selectedEmoji, themeColor, onSelectEmoji, onRandomize }) => {
  return (
    <div className="emoji-picker-container">
      <div className="lobby-section-label">Choose Emoji</div>
      <div 
        className="emoji-picker-grid"
        style={{ 
          borderColor: `${themeColor}40`,
          backgroundColor: `${themeColor}08`,
        }}
      >
        {emojis.length > 0 ? emojis.map((emoji) => {
          const isSelected = selectedEmoji === emoji;
          return (
            <button
              key={emoji}
              onClick={() => onSelectEmoji(emoji)}
              className={`emoji-picker-button ${isSelected ? 'selected' : ''}`}
              style={{
                borderColor: isSelected ? themeColor : `${themeColor}40`,
                borderWidth: isSelected ? '2px' : '1px',
                backgroundColor: isSelected ? `${themeColor}15` : 'white',
                boxShadow: isSelected 
                  ? `0 0 0 1px white, 0 2px 4px ${themeColor}50` 
                  : `0 1px 2px ${themeColor}20`,
              }}
              title={emoji}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.boxShadow = `0 2px 4px ${themeColor}40`;
                  e.currentTarget.style.border = `1px solid ${themeColor}80`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.boxShadow = `0 1px 2px ${themeColor}20`;
                  e.currentTarget.style.border = `1px solid ${themeColor}40`;
                }
              }}
            >
              {emoji}
            </button>
          );
        }) : (
          <div className="lobby-loading-text">Loading emojis...</div>
        )}
      </div>
      <div className="emoji-picker-randomize">
        <Button
          variant="outlined"
          size="small"
          onClick={onRandomize}
          className="lobby-button-small"
          style={{
            borderColor: themeColor,
            color: themeColor,
          }}
        >
          🎲 Randomize Emoji
        </Button>
      </div>
    </div>
  );
};

const CustomizeAvatarDialog = ({
  open,
  onClose,
  name,
  playerColor,
  playerEmoji,
  isReady,
  availableColors,
  availableEmojis,
  onChangePlayerColor,
  onChangePlayerEmoji,
  onRandomizePlayerColor,
  onRandomizePlayerEmoji,
  onRandomizeAll
}) => {
  const themeColor = getThemeColor(playerColor);

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title="🎨 Customize Your Avatar"
      maxWidth="sm"
      PaperProps={{
        className: "customize-avatar-dialog-paper"
      }}
      titleStyle={{
        className: "lobby-dialog-title"
      }}
      contentStyle={{
        className: "lobby-dialog-content"
      }}
      actions={
        <Button
          onClick={onClose}
          variant="contained"
          className="lobby-button-medium customize-avatar-done-button"
          style={{
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            color: 'white',
            boxShadow: `0 4px 12px ${themeColor}40`,
            border: `2px solid ${themeColor}`
          }}
        >
          Done
        </Button>
      }
      actionsStyle={{
        className: "customize-avatar-dialog-actions",
        borderTopColor: `${themeColor}30`
      }}
    >
        <div className="customize-avatar-content">
          <div className="customize-avatar-preview">
            <PlayerAvatar
              playerName={name}
              playerColor={playerColor}
              playerEmoji={playerEmoji}
              isReady={isReady}
              showReadyIndicator={false}
              size="large"
            />
            <Button
              variant="contained"
              onClick={onRandomizeAll}
              className="lobby-button-medium customize-avatar-randomize-all"
              style={{
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                color: 'white',
                boxShadow: `0 4px 12px ${themeColor}40`,
                border: `2px solid ${themeColor}`
              }}
            >
              🎲 Randomize All
            </Button>
          </div>

          <ColorPicker
            colors={availableColors}
            selectedColor={playerColor}
            themeColor={themeColor}
            onSelectColor={onChangePlayerColor}
            onRandomize={onRandomizePlayerColor}
          />

          <EmojiPicker
            emojis={availableEmojis}
            selectedEmoji={playerEmoji}
            themeColor={themeColor}
            onSelectEmoji={onChangePlayerEmoji}
            onRandomize={onRandomizePlayerEmoji}
          />
        </div>
    </CommonDialog>
  );
};

export default CustomizeAvatarDialog;

